import { getIsVinculacaoCuidadoAtiva } from '@atendimento/detail/soap/plano/cuidado-compartilhado/cuidado-compartilhado-form/utils-cuidadoCompartilhado'
import { SolicitacaoOciModel } from '@soap/plano/exames/model-oci'
import { Heading, InfoLabel, Text, VFlow } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import {
  AtendimentoProfissionalOdonto,
  EncaminhamentoExternoEspecializado,
  EvolucaoPlano,
  ReceitaMedicamento,
  SolicitacaoExame,
  TipoEncaminhamentoExternoEnum,
} from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { stringGrouper } from 'util/string/string-grouper'
import {
  encaminhamentoExternoRecord,
  historicoSubsectionStyles,
} from 'view/atendimentos/detail/historico/model/model-historico'
import { naoRegistradoNesseAtendimento } from 'view/atendimentos/detail/historico/utils/utils-historico'
import { PlanoCuidadoModel } from 'view/atendimentos/detail/soap/plano/observacao/model'
import { formatPlanoCuidadoData } from 'view/atendimentos/detail/soap/plano/utils/util-plano'

import HistoricoEvolucoesOdontologicasPanel from '../../atendimento-odontologico/pec/evolucoes-odontologicas/HistoricoEvolucoesOdontologicasPanel'
import HistoricoPlanoPecCuidadoCompartilhadoList, {
  HistoricoPlanoPecCuidadoCompartilhado,
} from './soap/plano/HistoricoPlanoPecCuidadoCompartilhadoList'
import HistoricoPlanoPecEncaminhamentosEspecializadosPanel from './soap/plano/HistoricoPlanoPecEncaminhamentosEspecializadosPanel'
import HistoricoPlanoPecExamesSolicitadosPanel from './soap/plano/HistoricoPlanoPecExamesSolicitadosPanel'
import HistoricoPlanoPecGuiaEncaminhamentoList, {
  HistoricoPlanoPecGuiaEncaminhamento,
} from './soap/plano/HistoricoPlanoPecGuiaEncaminhamentoList'
import HistoricoPlanoPecIntervencoesProcedimentosPanel from './soap/plano/HistoricoPlanoPecIntervencoesProcedimentosPanel'
import HistoricoPlanoPecPrescricaoPanel from './soap/plano/HistoricoPlanoPecPrescricaoPanel'
import HistoricoPlanoSolicitacoesOciPanel from './soap/plano/HistoricoPlanoSolicitacoesOciPanel'

interface HistoricoPlanoPecPanelProps {
  isAtendProcedimento?: boolean
  isAtendObservacao?: boolean
  evolucaoPlano: EvolucaoPlano
  atendProfOdonto?: AtendimentoProfissionalOdonto
  quantidadeAtestados?: number
  quantidadeOrientacoes?: number
  encaminhamentosEspecializados?: EncaminhamentoExternoEspecializado[]
  encaminhamentos?: ReadonlyArray<TipoEncaminhamentoExternoEnum>
  prescricaoMedicamentos?: ReceitaMedicamento[]
  examesSolicitados?: SolicitacaoExame[]
  planosDeCuidado?: ReadonlyArray<PlanoCuidadoModel>
  isOrigemApp?: boolean
  cuidadosCompartilhados?: ReadonlyArray<HistoricoPlanoPecCuidadoCompartilhado>
  guiasEncaminhamento?: ReadonlyArray<HistoricoPlanoPecGuiaEncaminhamento>
  solicitacoesOci?: ReadonlyArray<SolicitacaoOciModel>
}

export default function HistoricoPlanoPecPanel(props: HistoricoPlanoPecPanelProps) {
  const {
    evolucaoPlano,
    atendProfOdonto,
    quantidadeAtestados,
    quantidadeOrientacoes,
    encaminhamentosEspecializados,
    examesSolicitados,
    prescricaoMedicamentos,
    isAtendProcedimento,
    isAtendObservacao,
    isOrigemApp,
    encaminhamentos,
    planosDeCuidado,
    cuidadosCompartilhados,
    guiasEncaminhamento,
    solicitacoesOci,
  } = props

  const isAtendimentoOdonto = !isUndefinedOrNull(atendProfOdonto)
  const flags = useFlags()
  const isVinculacaoCuidadoCompartilhadoAtiva = getIsVinculacaoCuidadoAtiva(flags, isAtendimentoOdonto)

  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()

  const descricao = evolucaoPlano?.descricao?.removeTags()

  const possuiOrientacoes = quantidadeOrientacoes > 0

  const possuiAtestados = quantidadeAtestados > 0

  const possuiGuiasEncaminhamentos = isVinculacaoCuidadoCompartilhadoAtiva && guiasEncaminhamento?.isNotEmpty()

  const possuiCuidadosCompartilhados = cuidadosCompartilhados?.isNotEmpty()

  const hasPlano =
    descricao ||
    evolucaoPlano?.intervencoesProcedimentos ||
    atendProfOdonto ||
    examesSolicitados ||
    prescricaoMedicamentos ||
    encaminhamentos ||
    encaminhamentosEspecializados ||
    possuiAtestados ||
    possuiOrientacoes ||
    planosDeCuidado?.isNotEmpty() ||
    possuiGuiasEncaminhamentos ||
    possuiCuidadosCompartilhados

  return (
    <Fragment>
      {hasPlano ? (
        <VFlow>
          {isAtendObservacao ? (
            <VFlow vSpacing={0.5}>
              <Heading style={historicoSubsectionStyles.plano} level={5}>
                Planos de cuidado dessa observação
              </Heading>
              {planosDeCuidado?.map((planoCuidado) => (
                <VFlow vSpacing={0} key={planoCuidado.id}>
                  <Text fontWeight='bold'>{formatPlanoCuidadoData(planoCuidado.dataDescricao, now)}</Text>
                  <Text dangerouslySetInnerHTML={{ __html: planoCuidado.descricao }} />
                </VFlow>
              ))}
            </VFlow>
          ) : (
            descricao && <Text dangerouslySetInnerHTML={{ __html: evolucaoPlano?.descricao }} />
          )}

          {evolucaoPlano?.intervencoesProcedimentos && (
            <VFlow vSpacing={0.5}>
              <Heading style={historicoSubsectionStyles.plano} level={5}>
                Intervenções e/ou procedimentos clínicos
              </Heading>
              <HistoricoPlanoPecIntervencoesProcedimentosPanel
                intervencoesProcedimentos={evolucaoPlano.intervencoesProcedimentos}
              />
            </VFlow>
          )}
          {!isAtendProcedimento && !isOrigemApp && (
            <InfoLabel titleStyles={historicoSubsectionStyles.plano} title='Atestado'>
              <Text fontStyle='italic'>
                {quantidadeAtestados > 0
                  ? `${quantidadeAtestados > 1 ? 'Foram emitidos' : 'Foi emitido'} ${'atestado'.pluralizeAndConcatValue(
                      quantidadeAtestados
                    )} neste atendimento.`
                  : 'Não foram emitidos atestados neste atendimento.'}
              </Text>
            </InfoLabel>
          )}
          {examesSolicitados && (
            <VFlow vSpacing={0.5}>
              <Heading style={historicoSubsectionStyles.plano} level={5}>
                Exames solicitados
              </Heading>
              <HistoricoPlanoPecExamesSolicitadosPanel examesSolicitados={examesSolicitados} />
            </VFlow>
          )}
          {solicitacoesOci && (
            <VFlow vSpacing={0.5}>
              <Heading style={historicoSubsectionStyles.plano} level={5}>
                Ofertas de Cuidados Integrados solicitadas
              </Heading>
              <HistoricoPlanoSolicitacoesOciPanel solicitacoesOci={solicitacoesOci} />
            </VFlow>
          )}
          {prescricaoMedicamentos && (
            <VFlow vSpacing={0.5}>
              <Heading style={historicoSubsectionStyles.plano} level={5}>
                Medicamentos prescritos
              </Heading>
              <HistoricoPlanoPecPrescricaoPanel prescricaoMedicamentos={prescricaoMedicamentos} />
            </VFlow>
          )}

          {!isOrigemApp && (
            <InfoLabel titleStyles={historicoSubsectionStyles.plano} title='Orientações'>
              <Text fontStyle='italic'>
                {quantidadeOrientacoes > 0
                  ? `${quantidadeOrientacoes > 1 ? 'Foram emitidas' : 'Foi emitida'} ${quantidadeOrientacoes} ${
                      quantidadeOrientacoes > 1 ? 'orientações' : 'orientação'
                    } neste atendimento.`
                  : 'Não foram emitidas orientações neste atendimento.'}
              </Text>
            </InfoLabel>
          )}

          {(encaminhamentosEspecializados || encaminhamentos?.length > 0) && (
            <VFlow vSpacing={0.5}>
              <Heading style={historicoSubsectionStyles.plano} level={5}>
                Encaminhamentos
              </Heading>
              {encaminhamentos?.length > 0 && (
                <Text>
                  {stringGrouper(
                    ...encaminhamentos?.map((encaminhamento) => encaminhamentoExternoRecord[encaminhamento])
                  )}
                </Text>
              )}

              {encaminhamentosEspecializados && (
                <HistoricoPlanoPecEncaminhamentosEspecializadosPanel encaminhamentos={encaminhamentosEspecializados} />
              )}
            </VFlow>
          )}
          {(possuiCuidadosCompartilhados || possuiGuiasEncaminhamentos) && (
            <VFlow vSpacing={0.5}>
              <Heading style={historicoSubsectionStyles.plano} level={5}>
                {isVinculacaoCuidadoCompartilhadoAtiva
                  ? 'Encaminhamento/Compartilhamento de cuidado'
                  : 'Compartilhamento de cuidado'}
              </Heading>
              {possuiGuiasEncaminhamentos && (
                <HistoricoPlanoPecGuiaEncaminhamentoList guiasEncaminhamento={guiasEncaminhamento} />
              )}
              {possuiCuidadosCompartilhados && (
                <HistoricoPlanoPecCuidadoCompartilhadoList cuidadosCompartilhados={cuidadosCompartilhados} />
              )}
            </VFlow>
          )}
          {atendProfOdonto && <HistoricoEvolucoesOdontologicasPanel atendProfOdonto={atendProfOdonto} />}
        </VFlow>
      ) : (
        <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
      )}
    </Fragment>
  )
}
